'use client';

import { Box, type BoxProps } from '@mui/material';
import { type PropsWithChildren, useId, useState } from 'react';
import { useTooltipPosition } from './useTooltipPosition';
import { getTailStyles, HORIZONTAL_OFFSET, OPPOSITE_DIRECTION_MAP, VERTICAL_OFFSET } from './utils';

type Props = {
  tooltipContent: React.ReactNode;
  tooltipProps?: BoxProps & { 'data-testid'?: string };
  disableTouchEvents?: boolean;
};

const REVEALED_TOOLTIP_STYLES = {
  opacity: 1,
  visibility: 'visible',
  pointerEvents: 'auto',
} as const;

export const TooltipCSS = ({
  tooltipContent,
  children,
  tooltipProps,
  disableTouchEvents = false,
}: PropsWithChildren<Props>) => {
  const { sx: tooltipStyles, ...tooltipAdditionalProps } = tooltipProps || {};
  const [hoverPointerType, setHoverPointerType] = useState<string | null>(null);
  const tooltipId = useId();
  const { triggerRef, tooltipRef, position } = useTooltipPosition();
  const isLeftOrRight = ['left', 'right'].includes(position);
  const oppositeDirection = OPPOSITE_DIRECTION_MAP[position];

  const tailStyles = getTailStyles(position);

  // Stays null until a pointer enters, so the reveal is off until we know what the
  // user is pointing with. A browser that reports no pointerType is treated as
  // hover-capable rather than as a tap.
  const isHoverEnabled =
    !disableTouchEvents || (hoverPointerType !== null && hoverPointerType !== 'touch');
  const isVisible = hoverPointerType !== null && isHoverEnabled;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-block',
        '& > div:last-child': {
          opacity: 0,
          visibility: 'hidden',
          transition:
            'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out, transform 0.2s ease-in-out',
          pointerEvents: 'none',
        },
        ...(disableTouchEvents
          ? {
              // The media features describe the device, not the input in use, so hybrid
              // devices and DevTools emulation keep matching them while the user taps.
              // data-hover-enabled tracks the pointer that actually triggered the hover.
              '@media (hover: hover) and (pointer: fine)': {
                "&[data-hover-enabled='true']:hover > div:last-child": REVEALED_TOOLTIP_STYLES,
              },
            }
          : {
              '&:hover > div:last-child': REVEALED_TOOLTIP_STYLES,
            }),
        '--tooltip-tail-size': '11px',
        boxSizing: 'border-box',
      }}
      data-hover-enabled={isHoverEnabled}
      onPointerEnter={event => setHoverPointerType(event.pointerType)}
      onPointerLeave={() => setHoverPointerType(null)}
    >
      <Box ref={triggerRef} aria-describedby={`tooltip-${tooltipId}`}>
        {children}
      </Box>
      <Box
        ref={tooltipRef}
        aria-hidden={!isVisible}
        role="tooltip"
        id={`tooltip-${tooltipId}`}
        sx={{
          position: 'absolute',
          backgroundColor: 'white',
          borderRadius: 2,
          boxShadow:
            'rgba(0, 0, 0, 0.2) 0px 6px 6px -3px, rgba(0, 0, 0, 0.14) 0px 10px 14px 1px, rgba(0, 0, 0, 0.12) 0px 4px 18px 3px',
          border: '1px solid',
          borderColor: 'grey.200',
          padding: 3,
          zIndex: 1000,
          minWidth: 200,
          maxWidth: 'min(90vw, 90dvw)',
          ...(isLeftOrRight
            ? {
                // Moved the tooltip half its height down so it's vertically centered
                bottom: '50%',
                transform: 'translateY(50%)',
                [oppositeDirection]: `calc(100% + ${HORIZONTAL_OFFSET}px)`,
              }
            : {
                // Moved the tooltip half the trigger's width to the left so it's horizontally centered
                right: '50%',
                transform: 'translateX(50%)',
                [oppositeDirection]: `calc(100% + ${VERTICAL_OFFSET}px)`,
              }),
          // Tooltip tail - will be positioned based on direction
          ...tailStyles,
          ...tooltipStyles,
        }}
        {...tooltipAdditionalProps}
      >
        {tooltipContent}
      </Box>
    </Box>
  );
};
